// @ts-nocheck

import React, { useCallback, useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../client';
import * as KLPoleChipIcons from '../assets/img/pole';
import { KLCollectibleStatuses, type KLCollectibleStatus } from '../types/collectible';

import { KLPoleViewContext } from './KLRound4';

const getIconPath = ({ status, notified }) => {
  if (notified) {
    if (status === KLCollectibleStatuses.PENDING) {
      return KLPoleChipIcons.KLWhitePendingIcon;
    }

    if (status === KLCollectibleStatuses.LOST) {
      return KLPoleChipIcons.KLWhiteCrossIcon;
    }
  } else {
    if (status === KLCollectibleStatuses.WON) {
      return KLPoleChipIcons.KLGreenValidIcon;
    }

    if (status === KLCollectibleStatuses.LOST) {
      return KLPoleChipIcons.KLRedCrossIcon;
    }

    if (status === KLCollectibleStatuses.PENDING) {
      return KLPoleChipIcons.KLYellowPendingIcon;
    }
  }
};

const BackgroundColorByStatus = {
  [KLCollectibleStatuses.WON]: '#56AF3F',
  [KLCollectibleStatuses.LOST]: '#EB5F59',
  [KLCollectibleStatuses.PENDING]: '#F5CC4A',
};

const getBackground = ({ $status, $notified }) => {
  if ($status === KLCollectibleStatuses.OPEN) {
    return '#000000';
  }

  if ($notified) {
    return BackgroundColorByStatus[$status] ?? '#000000';
  }

  return '#21212188';
};

const Wrapper = styled.div`
  position: relative;
  width: 2.75em;
  height: 2.75em;
  border-radius: 50%;
  font-family: 'BettingArchivoNarrow';

  display: flex;
  justify-content: center;
  align-items: center;

  transition: none 150ms ease-in-out;
  transition-property: background-color, opacity;

  &:not(:last-child) {
    margin-bottom: 0.5em;

    &:after {
      content: '';
      position: absolute;
      z-index: -1;
      background: #000000cc;
      width: 0.2em;
      height: 0.5em;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  ${({ $status, $notified, $isClickable, $isHighlighted }) => {
    return css`
      --kl-pole-chip-bg-color: ${getBackground({ $status, $notified })};

      opacity: ${$status === KLCollectibleStatuses.OPEN ? 0.75 : 1};
      background-color: var(--kl-pole-chip-bg-color);
      ${$isClickable
        ? css`
            cursor: pointer;
          `
        : css`
            pointer-events: none;
          `}

      &:before {
        content: '';
        position: absolute;
        height: calc(100% - 2px);
        width: calc(100% - 2px);
        left: 0;
        top: 0;
        z-index: -1;
        background: transparent;
        border-radius: 50%;
        box-shadow: 0px 0px 10px 6px var(--kl-pole-chip-bg-color);
        transition: opacity 200ms ease-in-out;
        filter: contrast(200%);
        transform: translate(1px, 1px);

        ${$isHighlighted
          ? css`
              opacity: 1;
            `
          : css`
              opacity: 0;
            `};
      }

      ${!Target.__MOBILE__
        ? css`
            &:hover {
              &:before {
                opacity: 1;
              }
            }
          `
        : undefined}
    `;
  }}
`;

const LabelWrapper = styled.div`
  text-align: center;
  line-height: 1em;

  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DefaultLabelTop = styled.div`
  font-size: 80%;
`;

const DefaultLabelBottom = styled.div`
  font-size: 110%;
  font-weight: bold;
`;

const WonLabelTop = styled.div`
  font-weight: bold;
  font-size: 110%;
`;

const WonLabelBottom = styled.div`
  font-size: 80%;
`;

const IMG_DIMENSION_RATIO = 0.7;

const IconImg = styled.img`
  width: ${IMG_DIMENSION_RATIO * 100}%;
  height: ${IMG_DIMENSION_RATIO * 100}%;

  ${({ $isPending }) => {
    if (!$isPending) {
      return css`
        width: ${IMG_DIMENSION_RATIO * 100 * 0.6}%;
        height: ${IMG_DIMENSION_RATIO * 100 * 0.6}%;
      `;
    }
  }}
`;

type KLPoleChipProps = {
  className?: string;
  index: number;
  status?: KLCollectibleStatus;
  notified?: boolean;
  time?: number;
};

const KLPoleChip = ({
  className = '',
  index,
  notified = false,
  status = KLCollectibleStatuses.OPEN,
  time = null,
}: KLPoleChipProps) => {
  const { onChipClick, lastSelectedChipIndex, chipDetailsPanelIsOpened } = useContext(KLPoleViewContext);

  const Content = useMemo(() => {
    let rendered = null;

    if (status === KLCollectibleStatuses.OPEN) {
      rendered = (
        <LabelWrapper>
          <DefaultLabelTop>Pari</DefaultLabelTop>
          <DefaultLabelBottom>{index + 1}</DefaultLabelBottom>
        </LabelWrapper>
      );
    } else if (status === KLCollectibleStatuses.WON && notified) {
      rendered = (
        <LabelWrapper>
          <WonLabelTop>{time}</WonLabelTop>
          <WonLabelBottom>min</WonLabelBottom>
        </LabelWrapper>
      );
    } else {
      const iconPath = getIconPath({ status, notified });

      rendered = <IconImg src={iconPath} $isPending={status === KLCollectibleStatuses.PENDING} />;
    }

    return () => rendered;
  }, [index, notified, status, time]);

  const onClick = useCallback(
    (e) => {
      if (chipDetailsPanelIsOpened === false) {
        e.stopPropagation();

        if (onChipClick) {
          onChipClick(index);
        }
      }
    },
    [chipDetailsPanelIsOpened, index, onChipClick],
  );

  const isHighlighted = useMemo(
    () => lastSelectedChipIndex === index && chipDetailsPanelIsOpened,
    [chipDetailsPanelIsOpened, index, lastSelectedChipIndex],
  );

  const isClickable = useMemo(() => !!onChipClick && status !== KLCollectibleStatuses.OPEN, [onChipClick, status]);

  return (
    <Wrapper
      $status={status}
      $notified={notified}
      className={className}
      $isClickable={isClickable}
      onClick={onClick}
      $isHighlighted={isHighlighted}
    >
      <Content />
    </Wrapper>
  );
};

export default KLPoleChip;
